import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import MetaTags from "react-meta-tags";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Alert,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import {
  getInstrumentReportLab,
  updateRoundInstrument,
} from "store/databaseofunits/actions";
import "assets/scss/table.scss";

// ─────────────────────────────────────────────
// Date Helpers
// ─────────────────────────────────────────────

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatDate = value => {
  if (!value) return "NS";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return "NS";
  }

  const day = String(d.getDate()).padStart(2, "0");
  const month = MONTH_ABBR[d.getMonth()];
  const year = String(d.getFullYear()).slice(-2);

  return `${day}-${month}-${year}`;
};

// Qualitative analytes don't display Unit
const showUnit = round => !round.is_qualitative;

// ─────────────────────────────────────────────
// Group Rounds
// ─────────────────────────────────────────────
// Grouped so the table reads Scheme by Scheme: every round for a given
// scheme sits together (ordered by Cycle No, then Round No within it)
// before moving on to the next scheme — never interleaved by date across
// different schemes.

const getRoundsGrouped = selectedRounds => {
  const grouped = {};
  const order = [];

  selectedRounds.forEach(round => {
    // Group by round + which specific equipment ran it. A round tested
    // on multiple instruments (different analytes on different
    // equipment) needs to become multiple rows — one per equipment —
    // each scoped to just that equipment's own analytes, rather than
    // one row blending every instrument's results together.
    const equipmentName = round.instrument_name || null;
    const equipmentKeyPart = equipmentName
      ? equipmentName.toLowerCase()
      : "__ns__";
    const key = `${round.id}__${equipmentKeyPart}`;

    if (!grouped[key]) {
      grouped[key] = {
        id: round.id,
        rowKey: key,
        lab_id: round.lab_id,
        scheme_id: round.scheme_id,
        scheme_name: round.scheme_name,
        cycle_no: round.cycle_no,
        round_no: round.round_no,
        round_name: round.name,
        round_start_date: round.round_start_date,
        round_closing_date: round.round_closing_date,
        round_status: round.round_status,
        equipment_name: equipmentName,
        rows: [],
      };

      order.push(key);
    }

    grouped[key].rows.push(round);
  });

  const groups = order.map(key => {
    const group = grouped[key];

    const counts = {
      total: group.rows.length,
      accepted: 0,
      rejected: 0,
      warning: 0,
      submitted: 0,
      not_submitted: 0,
      abrogated: 0,
    };

    group.rows.forEach(row => {
      switch (row.evaluation_status) {
        case "Acceptable":
          counts.accepted += 1;
          break;

        case "Rejected":
          counts.rejected += 1;
          break;

        case "Warning":
          counts.warning += 1;
          break;

        case "Submitted":
          counts.submitted += 1;
          break;

        case "Not Submitted":
          counts.not_submitted += 1;
          break;

        case "Abrogated":
          counts.abrogated += 1;
          break;

        default:
          break;
      }
    });

    return {
      ...group,
      counts,
    };
  });

  // ── Scheme first, then Cycle No, then Round No, then Equipment ──
  // (NOT sorted by date — that's what was interleaving schemes.) Sorting
  // by equipment name last just keeps a round's multiple equipment rows
  // in a stable, predictable order relative to each other — it doesn't
  // affect the Scheme/Cycle/Round ordering above it.

  const schemeKey = value => (value == null ? "" : String(value).toLowerCase());

  const numericKey = value => {
    const n = Number(value);
    // Unknown/NS values sort to the end within their scheme+cycle.
    return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
  };

  // round_no is often a composite code (e.g. "571a-0304", "571a-03010"),
  // not a plain number — Number(round_no) is NaN for these, which is why
  // a straight numeric sort couldn't order them and fell back to
  // whatever order the API happened to return them in. A natural sort
  // compares the digit runs embedded in the string as numbers (so
  // "...-0309" sorts before "...-03010"), which orders these codes the
  // same way the underlying round numbers actually increase. Blank/NS
  // values are pushed to the end.
  const compareRoundNo = (a, b) => {
    const strA = a == null || a === "" ? null : String(a);
    const strB = b == null || b === "" ? null : String(b);

    if (strA === null && strB === null) return 0;
    if (strA === null) return 1;
    if (strB === null) return -1;

    return strA.localeCompare(strB, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  return groups.sort((a, b) => {
    const schemeCompare = schemeKey(a.scheme_name).localeCompare(
      schemeKey(b.scheme_name)
    );

    if (schemeCompare !== 0) return schemeCompare;

    const cycleCompare = numericKey(a.cycle_no) - numericKey(b.cycle_no);

    if (cycleCompare !== 0) return cycleCompare;

    const roundCompare = compareRoundNo(a.round_no, b.round_no);

    if (roundCompare !== 0) return roundCompare;

    return schemeKey(a.equipment_name).localeCompare(
      schemeKey(b.equipment_name)
    );
  });
};

// ─────────────────────────────────────────────
// Equipment Options
// ─────────────────────────────────────────────
// value is the instrument_id (not the name) — the backend needs the FK
// id to reassign Result.instrument, the name alone isn't enough. This
// is the one place we can trust the id: Result rows in Redux carry a
// real instrument_id, whereas equipment lists handed in via navigation
// state from other pages have sometimes turned out to only carry the
// display name as `value`.

const getEquipmentOptions = participants => {
  const seen = new Map();

  participants.forEach(participant => {
    (participant.results || []).forEach(result => {
      const name = result.instrument_name;

      if (!name) return;

      const key = name.toLowerCase();

      if (!seen.has(key)) {
        seen.set(key, {
          // The Result serializer exposes the FK's pk under the field's
          // own name — `instrument` — not `instrument_id`. `instrument_name`
          // is a separate, read-only display string (source='instrument.name').
          // Prefer the real pk when the result row has one; fall back to the
          // name so the option still shows in the dropdown — hasResolvedId
          // flags which case this is, so resolveInstrumentId knows whether
          // it can trust this value or still needs to look elsewhere for the id.
          value: result.instrument_id || name,
          hasResolvedId: Boolean(result.instrument_id),
          label: name,
          instrument_type: result.instrument_type,
          manufacturer: result.manufacturer,
        });
      }
    });
  });

  return Array.from(seen.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
};
// A numeric-looking value is (probably) a real instrument_id already.
// Anything else — most commonly the instrument's display name — needs
// resolving against the id map derived from Redux before it's usable.
const looksLikeId = value => /^\d+$/.test(String(value ?? "").trim());

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

class RoundsParticipatedPage extends Component {
  constructor(props) {
    super(props);

    const navState = props.location.state || {};
    const routeParams = props.match?.params || {};

    this.state = {
      modalType: navState.modalType || "participated",

      selectedEquipmentName: navState.equipmentName || null,

      selectedLabName: navState.labName || null,

      // Try nav state first, then common route-param names in case this
      // page is reached via something like /rounds-participated/:labId
      // rather than (or in addition to) location.state.
      selectedLabId:
        navState.labId ||
        navState.lab_id ||
        routeParams.labId ||
        routeParams.lab_id ||
        routeParams.id ||
        null,

      selectedRounds: navState.rounds || [],

      // Equipment options handed to us directly by the page that linked
      // here. If this is empty, componentDidMount below fetches the data
      // itself rather than silently relying on Redux already having it.
      navEquipmentOptions: navState.equipmentOptions || [],

      activeChangeRoundGroup: null,

      pendingEquipmentSelection: {},

      equipmentReassignments: {},

      // Set when confirmEquipmentReassignment can't resolve a real
      // lab_id or instrument_id and has to block the request rather
      // than send the backend a payload it will 400 on.
      reassignmentBlockedReason: null,
    };

    // Cycled through per scheme block (by index) so each scheme's header
    // gets a visually distinct background from the one above/below it.
    this.schemeHeaderColors = [
      { bg: "#eef2ff", text: "#4338ca" }, // indigo
      { bg: "#ecfdf5", text: "#047857" }, // green
      { bg: "#fff7ed", text: "#c2410c" }, // orange
      { bg: "#fef2f2", text: "#b91c1c" }, // red
      { bg: "#f0f9ff", text: "#0369a1" }, // sky
      { bg: "#faf5ff", text: "#7e22ce" }, // purple
    ];
  }

  componentDidMount() {
    // If nothing was handed to us via navigation state AND Redux doesn't
    // already have participant/equipment data, fetch it ourselves. This
    // makes the equipment dropdown work regardless of how this page was
    // reached (direct link, refresh, stale Redux, missing nav state, etc).
    const { navEquipmentOptions } = this.state;
    const { participants, onGetInstrumentReportList } = this.props;

    if (navEquipmentOptions.length === 0 && participants.length === 0) {
      const authUser = localStorage.getItem("authUser");
      const user_id = authUser ? JSON.parse(authUser).user_id : null;

      if (user_id && onGetInstrumentReportList) {
        onGetInstrumentReportList(user_id);
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          "RoundsParticipatedPage: could not resolve user_id to fetch equipment data."
        );
      }
    }
  }

  componentDidUpdate(prevProps) {
    // Once the API call that backs "Yes, Change" resolves, `participants`
    // (Redux) already carries the new instrument_name for every result
    // row in that round — the reducer patched it in place. Mirror the
    // confirmed name into local `equipmentReassignments` so the modal's
    // "currently assigned" label stays correct on the next open, and
    // clear the loading round.
    const { instrumentUpdateLoading: wasLoading } = prevProps;
    const {
      instrumentUpdateLoading: isLoading,
      instrumentUpdateError,
      lastUpdatedRound,
    } = this.props;

    if (
      wasLoading &&
      !isLoading &&
      !instrumentUpdateError &&
      lastUpdatedRound
    ) {
      const { round_id, instrument_name } = lastUpdatedRound;

      this.setState(prev => ({
        equipmentReassignments: {
          ...prev.equipmentReassignments,
          [round_id]: instrument_name,
        },
      }));
    }
  }

  // ─────────────────────────────────────────
  // Navigation
  // ─────────────────────────────────────────

  goBack = () => {
    this.props.history.goBack();
  };

  // ─────────────────────────────────────────
  // Change Equipment Modal
  // ─────────────────────────────────────────

  openChangeModal = roundGroup => {
    this.setState({
      activeChangeRoundGroup: roundGroup,
      reassignmentBlockedReason: null,
    });
  };

  closeChangeModal = () => {
    this.setState({
      activeChangeRoundGroup: null,
      reassignmentBlockedReason: null,
    });
  };

  handleEquipmentSelect = (roundId, instrumentId) => {
    this.setState(prev => ({
      pendingEquipmentSelection: {
        ...prev.pendingEquipmentSelection,
        [roundId]: instrumentId,
      },
    }));
  };

  // Best-effort lab_id resolution. Tries, in order: the round group
  // itself, the labId handed in via navigation state, a round from
  // selectedRounds that happens to carry it, then finally matching
  // selectedLabName against the labs Redux already knows about (each
  // `participant` entry is one lab, keyed by lab_id in the reducer).
  resolveLabId = roundGroup => {
    const { selectedLabId, selectedLabName, selectedRounds } = this.state;
    const { match, participants } = this.props;

    console.log("========== LAB ID DEBUG ==========");
    console.log("1. roundGroup:", roundGroup);
    console.log("2. roundGroup.lab_id:", roundGroup?.lab_id);
    console.log("3. selectedLabId:", selectedLabId);
    console.log("4. selectedLabName:", selectedLabName);
    console.log("5. selectedRounds:", selectedRounds);
    console.log("6. participants:", participants);
    console.log("7. route params:", match?.params);

    if (roundGroup?.lab_id) {
      console.log("LAB ID FOUND FROM roundGroup:", roundGroup.lab_id);
      return roundGroup.lab_id;
    }

    if (selectedLabId) {
      console.log("LAB ID FOUND FROM selectedLabId:", selectedLabId);
      return selectedLabId;
    }

    const routeParams = match?.params || {};

    if (routeParams.labId || routeParams.lab_id || routeParams.id) {
      const labId = routeParams.labId || routeParams.lab_id || routeParams.id;

      console.log("LAB ID FOUND FROM ROUTE:", labId);
      return labId;
    }

    const roundWithLabId = selectedRounds.find(round => round.lab_id);

    if (roundWithLabId) {
      console.log("LAB ID FOUND FROM selectedRounds:", roundWithLabId.lab_id);
      return roundWithLabId.lab_id;
    }

    console.error("❌ LAB ID NOT FOUND ANYWHERE");
    console.error("roundGroup:", roundGroup);
    console.error("selectedRounds:", selectedRounds);
    console.error("selectedLabId:", selectedLabId);
    console.error("routeParams:", routeParams);

    return null;
  };
  // The value coming out of the <select> may already be a real
  // instrument_id, or — if it was populated from navEquipmentOptions
  // handed in by whatever page linked here — may only be the
  // instrument's display name. Resolve it against what Redux actually
  // knows (result.instrument_id) before it goes anywhere near the API.
  resolveInstrumentId = selectedEquipment => {
    if (!selectedEquipment) return null;

    if (looksLikeId(selectedEquipment)) {
      return Number(selectedEquipment);
    }

    const equipmentName =
      typeof selectedEquipment === "string"
        ? selectedEquipment
        : selectedEquipment.name ||
          selectedEquipment.instrument_name ||
          selectedEquipment.equipment_name;

    if (!equipmentName) return null;

    const matched = this.getAvailableEquipmentOptions().find(
      option =>
        String(option.label).trim().toLowerCase() ===
        String(equipmentName).trim().toLowerCase()
    );

    return matched && looksLikeId(matched.value) ? Number(matched.value) : null;
  };
  confirmEquipmentReassignment = () => {
    const { activeChangeRoundGroup, pendingEquipmentSelection } = this.state;
    const { onUpdateRoundInstrument } = this.props;

    if (!activeChangeRoundGroup) return;

    const rawPicked = pendingEquipmentSelection[activeChangeRoundGroup.id];

    if (!rawPicked) return;

    const labId = this.resolveLabId(activeChangeRoundGroup);
    const instrumentId = this.resolveInstrumentId(rawPicked);

    const problems = [];
    if (!labId) problems.push("which lab this round belongs to");
    if (!instrumentId) problems.push("the selected equipment's id");

    if (problems.length > 0) {
      this.setState({
        reassignmentBlockedReason: `Couldn't determine ${problems.join(
          " or "
        )}. Please reopen this page from the Equipment Report table and try again.`,
      });
      return;
    }

    onUpdateRoundInstrument({
      lab_id: labId,
      round_id: activeChangeRoundGroup.id,
      scheme_id: activeChangeRoundGroup.scheme_id,
      instrument_id: instrumentId,
    });

    this.setState(prev => {
      const clearedSelection = { ...prev.pendingEquipmentSelection };
      delete clearedSelection[activeChangeRoundGroup.id];

      // Drop every row belonging to this round from the local snapshot —
      // it now belongs to a different equipment and no longer matches
      // this page's "Equipment: <name>" heading.
      const remainingRounds = prev.selectedRounds.filter(
        round => round.id !== activeChangeRoundGroup.id
      );

      return {
        selectedRounds: remainingRounds,
        pendingEquipmentSelection: clearedSelection,
        activeChangeRoundGroup: null,
        reassignmentBlockedReason: null,
      };
    });
  };

  // ─────────────────────────────────────────
  // Equipment options: nav state → Redux → (fetch kicked off in mount)
  // ─────────────────────────────────────────

  getAvailableEquipmentOptions = () => {
    const { navEquipmentOptions } = this.state;
    const derivedFromParticipants = getEquipmentOptions(
      this.props.participants
    );

    if (navEquipmentOptions.length === 0) {
      return derivedFromParticipants;
    }

    // navEquipmentOptions came from whatever page linked here. In
    // practice its `value` has sometimes turned out to be the
    // instrument's display name rather than its id — normalize each
    // entry against what Redux actually knows before it reaches the
    // <select>, so the id (not the name) is what ends up selected.
    return navEquipmentOptions.map(option => {
      if (looksLikeId(option.value)) return option;

      const match = derivedFromParticipants.find(
        known =>
          known.hasResolvedId &&
          String(known.label).toLowerCase() ===
            String(option.label ?? option.value).toLowerCase()
      );

      return match ? { ...option, value: match.value } : option;
    });
  };

  // ─────────────────────────────────────────
  // Change Equipment Modal render
  // ─────────────────────────────────────────

  renderChangeEquipmentModal = () => {
    const {
      activeChangeRoundGroup,
      pendingEquipmentSelection,
      equipmentReassignments,
      selectedEquipmentName,
      reassignmentBlockedReason,
    } = this.state;
    const { instrumentUpdateLoading, instrumentUpdateError } = this.props;

    const isOpen = Boolean(activeChangeRoundGroup);

    const roundGroup = activeChangeRoundGroup || {};

    const currentEquipment =
      equipmentReassignments[roundGroup.id] ||
      roundGroup.equipment_name ||
      selectedEquipmentName ||
      "NS";

    const pending = pendingEquipmentSelection[roundGroup.id] || "";

    const equipmentOptions = this.getAvailableEquipmentOptions();

    return (
      <Modal isOpen={isOpen} toggle={this.closeChangeModal} centered>
        <ModalHeader toggle={this.closeChangeModal}>
          Change Equipment
        </ModalHeader>

        <ModalBody>
          <p className="mb-3">
            Do you want to change this <strong>{currentEquipment}</strong>{" "}
            equipment from the round{" "}
            <strong>{roundGroup.round_name || "NS"}</strong> in all analytes?
          </p>

          <div className="text-muted mb-1">New Equipment</div>

          <select
            className="form-select"
            value={pending}
            disabled={instrumentUpdateLoading}
            onChange={event =>
              this.handleEquipmentSelect(roundGroup.id, event.target.value)
            }
          >
            <option value="">
              {equipmentOptions.length === 0
                ? "No equipment available"
                : "Select equipment..."}
            </option>

            {equipmentOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
                {option.manufacturer ? ` (${option.manufacturer})` : ""}
              </option>
            ))}
          </select>

          {reassignmentBlockedReason && (
            <Alert color="warning" className="mt-3 mb-0 fs-6">
              {reassignmentBlockedReason}
            </Alert>
          )}

          {instrumentUpdateError && (
            <Alert color="danger" className="mt-3 mb-0 fs-6">
              {instrumentUpdateError}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            color="light"
            onClick={this.closeChangeModal}
            disabled={instrumentUpdateLoading}
          >
            Cancel
          </Button>

          <Button
            color="primary"
            disabled={!pending || instrumentUpdateLoading}
            onClick={this.confirmEquipmentReassignment}
          >
            {instrumentUpdateLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Changing...
              </>
            ) : (
              "Yes, Change"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  // ─────────────────────────────────────────
  // Last Round View
  // ─────────────────────────────────────────
  renderLastRound = () => {
    const { selectedRounds } = this.state;

    const roundInfo = selectedRounds[0] || {};

    return (
      <>
        <div className="d-flex justify-content-between align-items-start flex-wrap border-bottom pb-3 mb-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-2">
              <span className="text-muted fs-6">
                {roundInfo.scheme_name || "NS"}
              </span>

              {roundInfo.cycle_no != null && (
                <span
                  className="badge rounded-pill"
                  style={{
                    backgroundColor: "#eef2ff",
                    color: "#4338ca",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                >
                  Cycle {roundInfo.cycle_no}
                </span>
              )}
            </div>

            <div className="fw-bold fs-3 text-dark">
              {roundInfo.name || "NS"}
            </div>
          </div>

          <div
            className="text-md-end mt-3 mt-md-0 px-3 py-2"
            style={{
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              minWidth: "140px",
            }}
          >
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
              CLOSING DATE
            </div>
            <div className="fw-bold fs-6 text-dark">
              {formatDate(roundInfo.round_closing_date)}
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <Table bordered hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th className="fs-6">Analyte</th>
                <th className="fs-6">Unit</th>
                <th className="fs-6">Method</th>
                <th className="fs-6">Reagent</th>
                <th className="fs-6">Evaluation</th>
              </tr>
            </thead>

            <tbody>
              {selectedRounds.map((round, index) =>
                this.renderLastRoundRow(round, index)
              )}
            </tbody>
          </Table>
        </div>
      </>
    );
  };

  // Named row renderer for the "Last Round" table — kept as a separate
  // method (rather than an inline arrow returning JSX inside .map())
  // to avoid the react/display-name ESLint false positive.
  renderLastRoundRow = (round, index) => {
    let evaluationClass = "text-dark";

    if (round.evaluation_status === "Rejected") {
      evaluationClass = "text-danger";
    } else if (round.evaluation_status === "Acceptable") {
      evaluationClass = "text-success";
    } else if (round.evaluation_status === "Warning") {
      evaluationClass = "text-warning";
    }

    return (
      <tr key={`${round.id}_${index}`}>
        <td className="fs-6">{round.analyte_name || "NS"}</td>

        <td className="fs-6">{showUnit(round) ? round.unit || "NS" : "—"}</td>

        <td className="fs-6">{round.method || "NS"}</td>

        <td className="fs-6">{round.reagent || "NS"}</td>

        <td className={`fw-bold fs-6 ${evaluationClass}`}>
          {round.evaluation_status || "NS"}
        </td>
      </tr>
    );
  };

  // ─────────────────────────────────────────
  // Participated Rounds — single table, grouped Scheme by Scheme
  // ─────────────────────────────────────────

  renderParticipatedRounds = () => {
    const { selectedRounds } = this.state;

    // getRoundsGrouped already returns rounds sorted Scheme → Cycle No →
    // Round No, so consecutive rows can simply be bucketed by scheme
    // name without re-sorting.
    const rounds = getRoundsGrouped(selectedRounds);
    const schemeBlocks = this.groupRoundsByScheme(rounds);

    return (
      <>
        {schemeBlocks.map((block, index) =>
          this.renderSchemeBlock(block, index)
        )}
      </>
    );
  };

  // Buckets the already Scheme-sorted rounds into one entry per scheme,
  // each carrying just the rounds that belong to it — so each scheme can
  // be rendered as its own div/card instead of one long shared table.
  groupRoundsByScheme = rounds => {
    const blocks = [];

    rounds.forEach(roundGroup => {
      const schemeName = roundGroup.scheme_name || "NS";
      const lastBlock = blocks[blocks.length - 1];

      if (lastBlock && lastBlock.scheme_name === schemeName) {
        lastBlock.rows.push(roundGroup);
      } else {
        blocks.push({ scheme_name: schemeName, rows: [roundGroup] });
      }
    });

    return blocks;
  };

  // One scheme's card: a colored header bar (color cycles per scheme
  // index via this.schemeHeaderColors) followed by that scheme's own
  // table of rounds. Kept as a named method (rather than an inline arrow
  // returning JSX inside .map()) to avoid the react/display-name ESLint
  // false positive.
  renderSchemeBlock = (block, index) => {
    const palette = this.schemeHeaderColors;
    const colors = palette[index % palette.length];

    return (
      <div
        key={block.scheme_name}
        className="border rounded overflow-hidden mb-4"
      >
        <div
          className="px-3 py-2 fw-bold fs-6"
          style={{ backgroundColor: colors.bg, color: colors.text }}
        >
          {block.scheme_name}
        </div>

        <div className="table-responsive">
          <Table bordered hover className="mb-0 align-middle text-center">
            <thead className="table-light">
              <tr>
                <th className="fs-6">Cycle No</th>
                <th className="fs-6">Round No</th>
                <th className="fs-6">Equipment</th>
                <th className="fs-6">Total</th>
                <th className="fs-6">Accepted</th>
                <th className="fs-6">Rejected</th>
                <th className="fs-6">Warning</th>
                <th className="fs-6">Change</th>
              </tr>
            </thead>

            <tbody>
              {block.rows.map(roundGroup => this.renderRoundRow(roundGroup))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  // Named row renderer for a scheme block's table — kept as a separate
  // method (rather than an inline arrow returning JSX inside .map()) to
  // avoid the react/display-name ESLint false positive, and so the row
  // markup can be reused/tested independently later. Scheme is no longer
  // a column here — it's shown once as the block's header instead.
  renderRoundRow = roundGroup => {
    const { counts } = roundGroup;

    return (
      <tr key={roundGroup.rowKey}>
        <td className="fs-6">{roundGroup.cycle_no ?? "NS"}</td>

        <td className="fs-6">{roundGroup.round_no ?? "NS"}</td>

        <td className="fs-6">{roundGroup.equipment_name || "NS"}</td>

        <td className="fw-bold fs-6">{counts.total}</td>

        <td className="fw-bold fs-6 text-success">{counts.accepted}</td>

        <td className="fw-bold fs-6 text-danger">{counts.rejected}</td>

        <td className="fw-bold fs-6 text-warning">{counts.warning}</td>

        <td>
          <Button
            color="outline-primary"
            size="sm"
            onClick={() => this.openChangeModal(roundGroup)}
          >
            Change
          </Button>
        </td>
      </tr>
    );
  };

  // ─────────────────────────────────────────
  // Empty State
  // ─────────────────────────────────────────

  renderEmptyState = () => {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Labs" breadcrumbItem="ROUNDS PARTICIPATED" />

          <div className="mb-3">
            <Button color="secondary" onClick={this.goBack}>
              Back
            </Button>
          </div>

          <Card>
            <CardBody className="p-4">
              <div className="fs-5 mb-3">No round data available.</div>

              <div className="text-muted fs-6 mb-3">
                Please open this page from the Equipment Report table.
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  };

  // ─────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────

  render() {
    const {
      modalType,
      selectedEquipmentName,
      selectedLabName,
      selectedRounds,
    } = this.state;

    if (selectedRounds.length === 0) {
      return this.renderEmptyState();
    }

    return (
      <div className="page-content">
        <MetaTags>
          <title>Rounds Participated | NHS NEQAS</title>
        </MetaTags>

        <Container fluid>
          <Breadcrumbs title="Labs" breadcrumbItem="ROUNDS PARTICIPATED" />

          {/* Back button, left-aligned, below the breadcrumbs */}
          <div className="mb-3">
            <Button color="secondary" onClick={this.goBack}>
              Back
            </Button>
          </div>

          <Row className="justify-content-center">
            <Col lg="10" xl="10">
              <Card>
                <CardBody className="p-4">
                  {/* Page Heading */}
                  <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-4">
                    <div>
                      <div className="fw-bold fs-4 text-dark">
                        {selectedLabName || "NS"}
                      </div>

                      <div className="text-muted fs-6 mt-1">
                        Equipment:{" "}
                        <span className="fw-semibold text-dark">
                          {selectedEquipmentName || "NS"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Last Round */}
                  {modalType === "lastRound"
                    ? this.renderLastRound()
                    : this.renderParticipatedRounds()}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {this.renderChangeEquipmentModal()}
      </div>
    );
  }
}

// ─────────────────────────────────────────────
// PropTypes
// ─────────────────────────────────────────────

RoundsParticipatedPage.propTypes = {
  location: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
  participants: PropTypes.array,
  onGetInstrumentReportList: PropTypes.func,
  onUpdateRoundInstrument: PropTypes.func,
  instrumentUpdateLoading: PropTypes.bool,
  instrumentUpdateError: PropTypes.string,
  lastUpdatedRound: PropTypes.object,
};

// ─────────────────────────────────────────────
// Redux
// ─────────────────────────────────────────────

const mapStateToProps = state => ({
  participants: state.ListUnit?.InstrumentReportLabDetail?.participants || [],
  instrumentUpdateLoading: state.ListUnit?.instrumentUpdateLoading || false,
  instrumentUpdateError: state.ListUnit?.instrumentUpdateError || null,
  lastUpdatedRound: state.ListUnit?.lastUpdatedRound || null,
});

const mapDispatchToProps = dispatch => ({
  onGetInstrumentReportList: id => dispatch(getInstrumentReportLab(id)),
  onUpdateRoundInstrument: payload => dispatch(updateRoundInstrument(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RoundsParticipatedPage);
